import { useTranslation } from "react-i18next";
import type { CustomerOrder } from "../../../common/jsonRpcTypes";
import OrderItem from "./OrderItem";

interface OrderListProps {
  orders: CustomerOrder[];
  onDeleteOrder: (orderId: string) => void;
  onEditOrder?: (orderId: string) => void;
  submitting: boolean;
  currency: string;
  countryCode: string;
  hidePaid: boolean;
}

const OrderList: React.FC<OrderListProps> = ({
  orders,
  onDeleteOrder,
  onEditOrder,
  submitting,
  currency,
  countryCode,
  hidePaid,
}) => {
  const { t } = useTranslation();

  const activeOrders = orders.filter((order) => order.status !== "deleted");
  const pendingOrders = orders.filter((order) => order.status === "pending");
  const completedOrders = orders.filter((order) => order.status === "complete");
  const ordersRequiringPayment = activeOrders.filter((order) => {
    return !order.payment_ref;
  });

  if (orders.length === 0) {
    return (
      <div id="gc-myo-order-list">
        <p className="gcw:text-center">{t("no_orders_found", "No orders added yet.")}</p>
      </div>
    );
  }

  return (
    <div id="gc-myo-order-list" className="order-list">
      <div className="gcw:flex gcw:flex-col gcw:items-start  gcw:mb-4">
        <h3 className="gcw:text-subtitle gcw:font-bold">
          {t("pending_orders", "Orders placed")} {`(${activeOrders.length})`}
        </h3>
        {ordersRequiringPayment.length > 0 && (
          <span className="gcw:text-order-details">
            {t("orders_requiring_payment", "{{count}} order requires payment", {
              count: ordersRequiringPayment.length,
            })}
          </span>
        )}
      </div>

      {pendingOrders.map((order) => (
        <OrderItem
          key={order.id}
          order={order}
          onDelete={onDeleteOrder}
          onEdit={onEditOrder}
          canDelete={!submitting}
          canEdit={!submitting && !!onEditOrder}
          showActions={true}
          currency={currency}
          countryCode={countryCode}
          hidePaid={hidePaid}
        />
      ))}

      {completedOrders.map((order) => (
        <OrderItem
          key={order.id}
          order={order}
          onDelete={onDeleteOrder}
          canDelete={false}
          showActions={false}
          currency={currency}
          countryCode={countryCode}
          hidePaid={hidePaid}
        />
      ))}
    </div>
  );
};

export default OrderList;
